import React, { useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, LayersControl } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Map as MapIcon, Info, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import L from 'leaflet';

// Fix for default marker icon in React Leaflet
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

let DefaultIcon = L.icon({
    iconUrl: icon,
    shadowUrl: iconShadow,
    iconSize: [25, 41],
    iconAnchor: [12, 41]
});

L.Marker.prototype.options.icon = DefaultIcon;

const { BaseLayer } = LayersControl;

const CadastralMap = () => {
    const [center, setCenter] = useState([13.6929, -89.2182]); // San Salvador default

    return (
        <div className="space-y-4">
            <Card className="border-none shadow-lg overflow-hidden">
                <CardHeader className="bg-gray-50 border-b pb-4">
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                        <CardTitle className="flex items-center gap-2 text-[#1A3D5C]">
                            <MapIcon className="w-5 h-5" /> Consulta de Clave Catastral
                        </CardTitle>
                        <Button
                            className="bg-[#1A3D5C] hover:bg-[#15324d] text-white gap-2"
                            onClick={() => window.open('https://www.e.cnr.gob.sv/ClaveCatastral/', '_blank')}
                        >
                            <ExternalLink className="w-4 h-4" />
                            Abrir Portal Oficial CNR
                        </Button>
                    </div>
                </CardHeader>
                <CardContent className="p-0">
                    <Tabs defaultValue="info" className="w-full">
                        <div className="bg-gray-100 border-b px-4 py-2">
                            <TabsList className="grid w-full max-w-md grid-cols-2">
                                <TabsTrigger value="info">Información del Portal</TabsTrigger>
                                <TabsTrigger value="satellite">Vista Satelital</TabsTrigger>
                            </TabsList>
                        </div>

                        <TabsContent value="info" className="m-0 h-[600px] relative bg-gray-100">
                            <div className="absolute inset-0 flex flex-col">
                                <div className="bg-blue-50 p-2 text-xs text-center text-blue-800 border-b border-blue-100 flex items-center justify-center gap-2">
                                    <Info className="w-3 h-3" />
                                    Si el mapa no carga aquí, usa el botón externo.
                                    <Button
                                        variant="link"
                                        className="h-auto p-0 text-blue-700 underline font-semibold"
                                        onClick={() => window.open('https://www.e.cnr.gob.sv/ClaveCatastral/', '_blank')}
                                    >
                                        Abrir en nueva pestaña
                                    </Button>
                                </div>
                                <iframe
                                    src="https://www.e.cnr.gob.sv/ClaveCatastral/"
                                    className="w-full h-full border-0"
                                    title="Consulta de Clave Catastral CNR"
                                    sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
                                    loading="lazy"
                                />
                            </div>
                        </TabsContent>

                        <TabsContent value="satellite" className="m-0 h-[600px] relative z-0">
                            <MapContainer
                                center={center}
                                zoom={16}
                                style={{ height: '100%', width: '100%' }}
                                scrollWheelZoom={true}
                            >
                                <LayersControl position="topright">
                                    <BaseLayer checked name="Satélite Híbrido (Esri)">
                                        <TileLayer
                                            url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                                            attribution='Tiles &copy; Esri'
                                        />
                                    </BaseLayer>
                                    <BaseLayer name="Callejero (OSM)">
                                        <TileLayer
                                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                            attribution='&copy; OSM contributors'
                                        />
                                    </BaseLayer>
                                </LayersControl>

                                <Marker position={center}>
                                    <Popup>Ubicación de Referencia - San Salvador</Popup>
                                </Marker>
                            </MapContainer>

                            <div className="absolute bottom-4 left-4 z-[400] bg-white/95 backdrop-blur-sm p-3 rounded-lg shadow-lg max-w-xs text-xs border border-gray-200">
                                <div className="flex items-start gap-2">
                                    <Info className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                                    <p className="text-gray-700">
                                        <strong>Nota:</strong> Esta es una vista satelital de referencia.
                                        Para ver los límites catastrales oficiales, usa la pestaña "Información del Portal".
                                    </p>
                                </div>
                            </div>
                        </TabsContent>
                    </Tabs>
                </CardContent>
            </Card>
        </div>
    );
};

export default CadastralMap;
